import { useMemo, useState } from "react";
import { api } from "../../api/client.js";
import type { CreateThreatModelReproductionInput, ThreatModelWorkspace } from "../../api/types.js";
import { InlineError } from "../../components/AsyncState.js";

export interface ReproductionDraft {
  candidateKey: string;
  fixtureId: string;
  authorizationRef: string;
  authorizationExpiresAt: string;
}

function sameIdSet(left: readonly string[], right: readonly string[]): boolean {
  const normalizedLeft = [...new Set(left)].sort();
  const normalizedRight = [...new Set(right)].sort();
  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((value, index) => value === normalizedRight[index])
  );
}

export function reproductionCandidates(workspace: ThreatModelWorkspace) {
  return workspace.revisions.slice(-1).flatMap((revision) =>
    revision.elements.flatMap((element) => {
      const path = workspace.projection.attackPaths.items.find((candidate) =>
        sameIdSet(candidate.edgeIds, element.edgeIds),
      );
      return element.kind === "data_flow" &&
        element.edgeIds.length > 0 &&
        revision.graphSha256 === workspace.projection.graph.canonicalSha256 &&
        path
        ? [
            {
              key: `${revision.canonicalSha256}:${element.id}`,
              revisionSha256: revision.canonicalSha256,
              revision: revision.revision,
              elementId: element.id,
              title: element.title,
              edgeIds: element.edgeIds,
              attackPathSha256: path.canonicalSha256,
              crownJewelId: path.crownJewelId,
              basis: path.basis,
            },
          ]
        : [];
    }),
  );
}

export function reproductionDraftError(
  workspace: ThreatModelWorkspace,
  draft: ReproductionDraft,
  now: Date,
): string | null {
  if (
    !reproductionCandidates(workspace).some((candidate) => candidate.key === draft.candidateKey)
  ) {
    return "Select an accepted data-flow that exactly matches a current governed attack path.";
  }
  if (!workspace.reproductionFixtures.some((fixture) => fixture.id === draft.fixtureId)) {
    return "Select a custody-pinned reproduction fixture.";
  }
  if (!draft.authorizationRef.trim()) return "An authorization reference is required.";
  const expiry = Date.parse(draft.authorizationExpiresAt);
  if (!Number.isFinite(expiry) || expiry <= now.getTime()) {
    return "Authorization expiry must be a future RFC 3339 timestamp.";
  }
  return null;
}

export function reproductionInput(
  snapshotId: string,
  workspace: ThreatModelWorkspace,
  draft: ReproductionDraft,
): CreateThreatModelReproductionInput {
  const candidate = reproductionCandidates(workspace).find(
    (item) => item.key === draft.candidateKey,
  );
  if (!candidate) throw new Error("Accepted reproduction path is unavailable");
  return {
    snapshotId,
    threatModelRevisionSha256: candidate.revisionSha256,
    elementId: candidate.elementId,
    fixtureId: draft.fixtureId,
    authorizationRef: draft.authorizationRef.trim(),
    authorizationExpiresAt: new Date(draft.authorizationExpiresAt).toISOString(),
  };
}

function completedSummary(payloadJson: string): string {
  try {
    const payload = JSON.parse(payloadJson) as {
      status?: string;
      cleanup?: string;
      exitCode?: number | null;
    };
    return `${payload.status ?? "unknown"} · exit ${payload.exitCode ?? "none"} · cleanup ${payload.cleanup ?? "unknown"}`;
  } catch {
    return "invalid persisted payload";
  }
}

export function ReproductionPanel({
  workspace,
  canRun,
  onSaved,
}: {
  workspace: ThreatModelWorkspace;
  canRun: boolean;
  onSaved: () => void;
}) {
  const candidates = useMemo(() => reproductionCandidates(workspace), [workspace]);
  const [draft, setDraft] = useState<ReproductionDraft>({
    candidateKey: candidates.at(-1)?.key ?? "",
    fixtureId: workspace.reproductionFixtures[0]?.id ?? "",
    authorizationRef: "",
    authorizationExpiresAt: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const draftError = reproductionDraftError(workspace, draft, new Date());

  async function run() {
    if (draftError) return;
    setSaving(true);
    setError(null);
    try {
      await api.createThreatModelReproduction(
        reproductionInput(workspace.projection.graph.snapshotId, workspace, draft),
      );
      setDraft({ ...draft, authorizationRef: "", authorizationExpiresAt: "" });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error("Reproduction request failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="panel-card" aria-labelledby="reproduction-heading">
      <h2 id="reproduction-heading">Isolated path reproduction</h2>
      <p>
        Runs only a custody-pinned synthetic or public fixture for an accepted data-flow path. The
        worker has no host credentials or egress and is resource bounded. A failed reproduction
        never refutes a static finding, and a human-approved edge is never relabelled as dynamic
        evidence.
      </p>
      {canRun ? (
        candidates.length && workspace.reproductionFixtures.length ? (
          <div className="threat-model-editor">
            <label className="form-field">
              <span>Accepted graph path</span>
              <select
                value={draft.candidateKey}
                onChange={(event) => setDraft({ ...draft, candidateKey: event.target.value })}
              >
                {candidates.map((candidate) => (
                  <option key={candidate.key} value={candidate.key}>
                    Revision {candidate.revision} · {candidate.title} · {candidate.edgeIds.length}{" "}
                    edge(s) · {candidate.basis.replaceAll("_", " ")} · crown{" "}
                    {candidate.crownJewelId}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Custody-pinned fixture</span>
              <select
                value={draft.fixtureId}
                onChange={(event) => setDraft({ ...draft, fixtureId: event.target.value })}
              >
                {workspace.reproductionFixtures.map((fixture) => (
                  <option key={fixture.id} value={fixture.id}>
                    {fixture.id} · {fixture.kind}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Authorization reference</span>
              <input
                value={draft.authorizationRef}
                onChange={(event) => setDraft({ ...draft, authorizationRef: event.target.value })}
              />
            </label>
            <label className="form-field">
              <span>Authorization expiry · RFC 3339</span>
              <input
                placeholder="2026-08-24T13:00:00.000Z"
                value={draft.authorizationExpiresAt}
                onChange={(event) =>
                  setDraft({ ...draft, authorizationExpiresAt: event.target.value })
                }
              />
            </label>
            {draftError &&
            (draft.authorizationRef || draft.authorizationExpiresAt || draft.candidateKey) ? (
              <p className="field-error">{draftError}</p>
            ) : null}
            {error ? <InlineError error={error} onRetry={() => void run()} /> : null}
            <button
              className="button button-primary"
              type="button"
              disabled={saving || Boolean(draftError)}
              onClick={() => void run()}
            >
              {saving ? "Running…" : "Run isolated fixture"}
            </button>
          </div>
        ) : (
          <p>
            Unavailable. An accepted data-flow path and a configured, digest-pinned fixture are both
            required.
          </p>
        )
      ) : (
        <p>Read-only. Only an owner or risk manager may authorize reproduction.</p>
      )}
      <h3>Run custody</h3>
      {workspace.reproductionEvents.length ? (
        <ol className="revision-list" aria-label="Reproduction run custody">
          {workspace.reproductionEvents.map((event) => (
            <li key={event.id}>
              <strong>
                {event.runId} · {event.eventType}
              </strong>
              <span>
                {event.actorId} · {new Date(event.createdAt).toLocaleString()}
              </span>
              {event.eventType === "completed" ? (
                <span>{completedSummary(event.payloadJson)}</span>
              ) : null}
              <code>{event.canonicalSha256}</code>
            </li>
          ))}
        </ol>
      ) : (
        <p>No reproduction has been requested for this snapshot.</p>
      )}
    </article>
  );
}
